import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQueueStore } from "@/lib/store";
import { useAuthStore } from "@/lib/auth";
import { PatientStatus } from "@/types";
import { format } from "date-fns";
import { Clock, User, CheckCircle2, Clipboard, CheckCircle, Printer } from "lucide-react";
import { safeFormatDate, calculateDuration } from '@/lib/utils/dateUtils';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { printPatientOPSheet } from '@/lib/utils/printUtils';
import { printPrescription } from '@/lib/utils/printPrescription';
import { usePrescriptionsStore } from "@/lib/stores/prescriptions";

export function DoctorDashboard() {
  const queueStore = useQueueStore();
  const { user } = useAuthStore();
  const { toast } = useToast();
  const { getPrescriptionsByPatient } = usePrescriptionsStore();
  const [showAllPatients, setShowAllPatients] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<'yesterday' | 'today' | 'followups'>('today');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter patients for the current doctor and only show In Consultation and Completed status
  const allDoctorPatients = queueStore.patients.filter(
    (patient) => 
      patient.doctorId === user?.id && 
      (patient.status === "in-consultation" || patient.status === "completed")
  );

  // Date filtering logic
  const getDateRange = (range: 'yesterday' | 'today' | 'followups') => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (range) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: new Date(yesterday.getTime() + 24 * 60 * 60 * 1000 - 1) };
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1) };
      case 'followups':
        // For follow-ups, we don't use date range - we'll filter by follow-up date instead
        return { start: today, end: today };
      default:
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1) };
    }
  };

  const filterPatientsByDate = (patients: any[], range: 'yesterday' | 'today' | 'followups') => {
    if (showAllPatients) return patients;
    
    if (range === 'followups') {
      // Filter patients with follow-up dates in the next 7 days
      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);
      
      return patients.filter(patient => {
        if (!patient.followUpDate) return false;
        const followUpDate = new Date(patient.followUpDate);
        return followUpDate >= today && followUpDate <= nextWeek;
      });
    }
    
    const { start, end } = getDateRange(range);
    return patients.filter(patient => {
      const patientDate = new Date(patient.registeredAt);
      return patientDate >= start && patientDate <= end;
    });
  };

  // Default to today's patients
  const doctorPatients = filterPatientsByDate(allDoctorPatients, selectedDateRange);

  // Color-coded summary items
  const summaryItems = [
    {
      title: "Total Patients",
      value: doctorPatients.length,
      icon: User,
      bgColor: "bg-blue-100",
      color: "text-blue-800",
      iconColor: "text-blue-800",
      description: showAllPatients ? "All your patients" : "Your patients today"
    },
    {
      title: "In Consultation",
      value: doctorPatients.filter(p => p.status === "in-consultation").length,
      icon: Clock,
      bgColor: "bg-yellow-100",
      color: "text-yellow-800",
      iconColor: "text-yellow-800",
      description: "Currently consulting"
    },
    {
      title: "Completed",
      value: doctorPatients.filter(p => p.status === "completed").length,
      icon: CheckCircle2,
      bgColor: "bg-green-100",
      color: "text-green-800",
      iconColor: "text-green-800",
      description: "Consultations completed"
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "in-consultation":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            In Consultation
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Completed
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleCompletePatient = async (patientId: string) => {
    try {
      await queueStore.updatePatientStatus(patientId, "completed");
      toast({
        title: "Patient completed",
        description: "Patient consultation has been marked as completed",
      });
    } catch (error) {
      console.error("Error completing patient:", error);
      toast({
        title: "Error",
        description: "Failed to complete patient consultation",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queueStore.fetchPatients();
      toast({
        title: "Data refreshed",
        description: "Patient data has been updated"
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDateFilter = (range: 'yesterday' | 'today' | 'followups') => {
    setShowAllPatients(false);
    setSelectedDateRange(range);
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {summaryItems.map((item) => (
          <Card key={item.title} className={`${item.bgColor} border-0 shadow-sm`}>
            <CardContent className="p-4">
              <div className="space-y-2">
                {/* First row: Title (left) and Icon (right) */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">{item.title}</p>
                  <item.icon className={`h-5 w-5 ${item.iconColor}`} />
                </div>
                {/* Second row: Description (left) and Value (right) */}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {item.description}
                  </p>
                  <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle>Your Patients</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Button
                  variant={!showAllPatients && selectedDateRange === 'yesterday' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleDateFilter('yesterday')}
                >
                  Yesterday
                </Button>
                <Button
                  variant={!showAllPatients && selectedDateRange === 'today' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleDateFilter('today')}
                >
                  Today
                </Button>
                <Button
                  variant={!showAllPatients && selectedDateRange === 'followups' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleDateFilter('followups')}
                >
                  Follow-ups
                </Button>
              </div>
              <Button
                variant={showAllPatients ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAllPatients(!showAllPatients)}
              >
                Show All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2"
              >
                {isRefreshing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                ) : (
                  <Clock className="h-4 w-4" />
                )}
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {doctorPatients.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <div className="text-6xl mb-4">👨‍⚕️</div>
              <h3 className="text-lg font-medium mb-2">No patients found</h3>
              <p className="text-sm">
                {showAllPatients 
                  ? "No patients have been assigned to you yet."
                  : "No patients found for the selected date range."
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {doctorPatients.map((patient) => (
                <Card key={patient.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      {/* Left Section - Patient Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Link 
                            to={`/patient/${patient.id}`} 
                            className="hover:text-hospital-primary transition-colors"
                          >
                            <h3 className="text-lg font-medium">{patient.name}</h3>
                          </Link>
                          {getStatusBadge(patient.status)}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" />
                            <span>{patient.age} • {patient.gender}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            <span>Registered: {safeFormatDate(patient.registeredAt, "h:mm a")}</span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Reason: </span>
                          {patient.reason}
                        </p>
                        
                        {patient.status === "completed" && patient.completedAt && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span>Completed: {safeFormatDate(patient.completedAt, "h:mm a")}</span>
                            <span className="mx-2">•</span>
                            <span>Duration: {calculateDuration(patient.registeredAt, patient.completedAt)}</span>
                          </div>
                        )}
                      </div>

                      {/* Right Section - Actions */}
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          {/* Smart Print Button - OP Sheet or Prescription */}
                          {(() => {
                            const prescriptions = getPrescriptionsByPatient(patient.id);
                            const hasIssuedPrescriptions = prescriptions.some(p => p.status === 'issued');
                            
                            return hasIssuedPrescriptions ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => printPrescription(patient, prescriptions, queueStore.doctors)}
                                className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                title="Print Prescription"
                              >
                                <Printer className="h-4 w-4 mr-1" />
                                Print Prescription
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => printPatientOPSheet(patient, queueStore.doctors)}
                                className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                title="Print OP Sheet"
                              >
                                <Printer className="h-4 w-4 mr-1" />
                                Print OP Sheet
                              </Button>
                            );
                          })()}
                          
                          {patient.status === "in-consultation" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                asChild
                              >
                                <Link to={`/e-prescription/${patient.id}`}>
                                  <Clipboard className="h-4 w-4 mr-1" />
                                  E-Prescription
                                </Link>
                              </Button>
                              
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleCompletePatient(patient.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Complete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 